import { type QueryClient, queryOptions } from '@tanstack/react-query'
import { ApiError, api, call } from './api'

const noRetryOnClientError = (count: number, e: unknown) => !(e instanceof ApiError && e.status < 500 && e.status !== 0) && count < 2

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: () => call(api.GET('/me')),
  retry: noRetryOnClientError,
  staleTime: 60_000,
})

/** A Google picture is copied in the background and lands a few seconds
 *  after it's asked for (sc-2381: one refetch on a timer missed it, and the
 *  page looked as if the tap did nothing). Refetch /me until the picture is
 *  not the one from before, then refresh everything that shows a face.
 *  Resolves true when it landed, false when it hadn't within the wait. */
export async function awaitPicture(qc: QueryClient, before: string | null | undefined, tries = 20, every = 1500): Promise<boolean> {
  for (let i = 0; i < tries; i++) {
    await new Promise((r) => setTimeout(r, every))
    const me = await qc.fetchQuery({ ...meQuery, staleTime: 0 })
    if ((me.user.avatar_url ?? null) !== (before ?? null) && me.user.avatar_url) {
      qc.invalidateQueries()
      return true
    }
  }
  return false
}

export const leagueQuery = (slug: string) =>
  queryOptions({
    queryKey: ['league', slug],
    queryFn: () => call(api.GET('/leagues/{slug}', { params: { path: { slug } } })),
    retry: noRetryOnClientError,
    // Round phases flip at midnight; a gentle poll keeps an open tab honest.
    refetchInterval: 60_000,
  })

// The season-scoped queries take the season's number for the full-form
// paths of designs/20261017-seasons.md §4 (a season's own rounds, results
// and table, which stay where they are after the next season starts), and
// without one they mean the current season.
export const roundsQuery = (slug: string, season?: number) =>
  queryOptions({
    queryKey: ['league', slug, 'rounds', season ?? 'current'],
    queryFn: async () =>
      (season === undefined
        ? await call(api.GET('/leagues/{slug}/rounds', { params: { path: { slug } } }))
        : await call(api.GET('/leagues/{slug}/seasons/{season}/rounds', { params: { path: { slug, season } } }))
      ).rounds,
    retry: noRetryOnClientError,
  })

export const roundQuery = (slug: string, number: number) =>
  queryOptions({
    queryKey: ['league', slug, 'round', number],
    queryFn: () => call(api.GET('/leagues/{slug}/rounds/{number}', { params: { path: { slug, number } } })),
    retry: noRetryOnClientError,
  })

// The league's vote on next week's categories (sc-4082). 404 when there is
// none, which callers show as nothing rather than an error.
export const categoryVoteQuery = (slug: string) =>
  queryOptions({
    queryKey: ['league', slug, 'category-vote'],
    queryFn: async () => {
      try {
        return await call(api.GET('/leagues/{slug}/category-vote', { params: { path: { slug } } }))
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },
    retry: noRetryOnClientError,
    refetchInterval: 60_000,
  })

export const feedQuery = (categoryId: number) =>
  queryOptions({
    queryKey: ['feed', categoryId],
    queryFn: () => call(api.GET('/categories/{id}/submissions', { params: { path: { id: categoryId } } })),
    retry: noRetryOnClientError,
    refetchInterval: 30_000,
  })

export const ballotQuery = (categoryId: number) =>
  queryOptions({
    queryKey: ['ballot', categoryId],
    queryFn: () => call(api.GET('/categories/{id}/ballot', { params: { path: { id: categoryId } } })),
    retry: noRetryOnClientError,
  })

export const resultsQuery = (slug: string, number: number, season?: number) =>
  queryOptions({
    queryKey: ['league', slug, 'results', season ?? 'current', number],
    queryFn: () =>
      season === undefined
        ? call(api.GET('/leagues/{slug}/rounds/{number}/results', { params: { path: { slug, number } } }))
        : call(api.GET('/leagues/{slug}/seasons/{season}/rounds/{number}/results', { params: { path: { slug, season, number } } })),
    retry: noRetryOnClientError,
    staleTime: Infinity, // a final round never changes
  })

// The cats' all-time table (designs/20261028-cat-leaderboards.md).
export const catBoardQuery = (slug: string) =>
  queryOptions({
    queryKey: ['league', slug, 'cats'],
    queryFn: () => call(api.GET('/leagues/{slug}/cats', { params: { path: { slug } } })),
    retry: noRetryOnClientError,
  })

export const standingsQuery =(slug: string, season?: number) =>
  queryOptions({
    queryKey: ['league', slug, 'standings', season ?? 'current'],
    queryFn: () =>
      season === undefined
        ? call(api.GET('/leagues/{slug}/standings', { params: { path: { slug } } }))
        : call(api.GET('/leagues/{slug}/seasons/{season}/standings', { params: { path: { slug, season } } })),
    retry: noRetryOnClientError,
  })

/** Every season, oldest first; the last one is the current season: the
 *  running one, or in the off-season the last complete one, whose podium
 *  the server computes (designs/20261017-seasons.md §2). */
export const seasonsQuery = (slug: string) =>
  queryOptions({
    queryKey: ['league', slug, 'seasons'],
    queryFn: async () => (await call(api.GET('/leagues/{slug}/seasons', { params: { path: { slug } } }))).seasons ?? [],
    retry: noRetryOnClientError,
    refetchInterval: 60_000,
  })

export const petsQuery = queryOptions({
  queryKey: ['pets'],
  queryFn: async () => (await call(api.GET('/pets'))).pets ?? [],
  retry: noRetryOnClientError,
})

export const inviteQuery = (code: string) =>
  queryOptions({
    queryKey: ['invite', code],
    queryFn: () => call(api.GET('/invites/{code}', { params: { path: { code } } })),
    retry: false,
  })

/** The bell's count (designs/20261020-notification-center.md §4): asked on
 *  load, on focus and every minute while the page is visible. No sockets. */
export const unreadQuery = queryOptions({
  queryKey: ['notifications', 'unread'],
  queryFn: () => call(api.GET('/notifications/unread')),
  retry: noRetryOnClientError,
  refetchInterval: 60_000,
  refetchOnWindowFocus: true,
})

/** Which ways to sign in the server offers. */
export const providersQuery = queryOptions({ queryKey: ['auth-providers'], queryFn: () => call(api.GET('/auth/providers')), staleTime: Infinity })
